import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Space, App } from 'antd'
import { ZoomIn, ZoomOut, Download, Maximize2, Code } from 'lucide-react'
import mermaid from 'mermaid'
import { useUIStore } from '@/store/uiStore'

export interface MermaidRendererProps {
  chart: string
}

// Initialize mermaid with default config
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'arial, sans-serif',
})

export function MermaidRenderer({ chart }: MermaidRendererProps) {
  const { message } = App.useApp()
  const isDarkMode = useUIStore((state) => state.isDarkMode)
  const containerRef = useRef<HTMLDivElement>(null)
  const svgContainerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [svg, setSvg] = useState<string>('')
  const [zoom, setZoom] = useState(1)
  const [showCode, setShowCode] = useState(false)

  // Clean chart code for display
  const cleanedChart = chart
    .trim()
    .replace(/^```mermaid\s*\n?/i, '')
    .replace(/\n?```\s*$/, '')
    .trim()

  useEffect(() => {
    const renderDiagram = async () => {
      if (!chart) {
        return
      }

      try {
        setError(null)
        setSvg('') // Reset SVG while rendering

        // Clean the chart code - remove markdown code block markers
        let cleanChart = chart.trim()

        cleanChart = cleanChart.replace(/^```mermaid\s*\n?/i, '')
        cleanChart = cleanChart.replace(/\n?```\s*$/, '')
        cleanChart = cleanChart.trim()

        if (!cleanChart) {
          setError('Empty diagram code')
          return
        }

        // Generate unique ID for this diagram
        const id = `mermaid-${String(Date.now())}-${Math.random().toString(36).substring(2, 9)}`

        // Render the diagram
        const { svg: renderedSvg } = await mermaid.render(id, cleanChart)
        setSvg(renderedSvg)
      } catch (err) {
        console.error('Mermaid rendering error:', err)
        setError(err instanceof Error ? err.message : 'Failed to render diagram')
      }
    }

    void renderDiagram()
  }, [chart])

  if (error) {
    return <Alert type="error" showIcon description={error} style={{ marginTop: 12 }} />
  }

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.2, 3))
  }

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.2, 0.5))
  }

  const handleResetZoom = () => {
    setZoom(1)
  }

  const handleDownloadPNG = async () => {
    if (!svgContainerRef.current) return

    const svgElement = svgContainerRef.current.querySelector('svg')
    if (!svgElement) return

    try {
      // Clone SVG to avoid modifying the original
      const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement

      // Get SVG dimensions
      const bbox = svgElement.getBBox()
      const width = bbox.width || Number(svgElement.getAttribute('width')) || 800
      const height = bbox.height || Number(svgElement.getAttribute('height')) || 600

      // Set explicit dimensions on cloned SVG
      clonedSvg.setAttribute('width', String(width))
      clonedSvg.setAttribute('height', String(height))

      // Serialize SVG to string
      const svgData = new XMLSerializer().serializeToString(clonedSvg)

      // Create canvas
      const canvas = document.createElement('canvas')
      const scale = 2 // Higher scale for better quality
      canvas.width = width * scale
      canvas.height = height * scale
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        throw new Error('Failed to get canvas context')
      }

      // Scale context for higher quality
      ctx.scale(scale, scale)

      // Create image from SVG data URL (inline to avoid CORS)
      const img = new Image()
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(svgBlob)

      // Use promise to handle image loading
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          try {
            // Draw white background
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, width, height)

            // Draw image
            ctx.drawImage(img, 0, 0, width, height)
            URL.revokeObjectURL(url)
            resolve()
          } catch (err) {
            reject(err instanceof Error ? err : new Error(String(err)))
          }
        }
        img.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Failed to load SVG image'))
        }

        // Set crossOrigin to anonymous to avoid CORS issues
        img.crossOrigin = 'anonymous'
        img.src = url
      })

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = `diagram-${String(Date.now())}.png`
          link.click()
          URL.revokeObjectURL(link.href)
          void message.open({
            type: 'success',
            content: 'Diagram downloaded as PNG',
            duration: 2,
          })
        }
      }, 'image/png')
    } catch (error) {
      console.error('Failed to export PNG:', error)
      void message.open({
        type: 'error',
        content: 'Failed to export PNG. Try downloading as SVG instead.',
        duration: 3,
      })
    }
  }

  const handleDownloadSVG = () => {
    if (!svgContainerRef.current) return

    const svgElement = svgContainerRef.current.querySelector('svg')
    if (!svgElement) return

    const svgData = new XMLSerializer().serializeToString(svgElement)
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    const link = document.createElement('a')
    link.href = url
    link.download = `diagram-${String(Date.now())}.svg`
    link.click()
    URL.revokeObjectURL(url)

    void message.open({
      type: 'success',
      content: 'Diagram downloaded as SVG',
      duration: 2,
    })
  }

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(cleanedChart)
      void message.open({
        type: 'success',
        content: 'Diagram code copied to clipboard',
        duration: 2,
      })
    } catch (err) {
      console.error('Failed to copy:', err)
      void message.open({
        type: 'error',
        content: 'Failed to copy code',
        duration: 2,
      })
    }
  }

  if (!svg) {
    return <div style={{ padding: '12px', color: '#999' }}>Rendering diagram...</div>
  }

  return (
    <div ref={containerRef} style={{ marginTop: 12 }}>
      {/* Control buttons */}
      <Space
        size="small"
        style={{
          marginBottom: 8,
          padding: '8px 12px',
          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#fafafa',
          borderRadius: 8,
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
        }}
      >
        <Button
          size="small"
          icon={<ZoomIn size={14} />}
          onClick={handleZoomIn}
          disabled={zoom >= 3}
          title="Zoom in"
        />
        <Button
          size="small"
          icon={<ZoomOut size={14} />}
          onClick={handleZoomOut}
          disabled={zoom <= 0.5}
          title="Zoom out"
        />
        <Button
          size="small"
          icon={<Maximize2 size={14} />}
          onClick={handleResetZoom}
          disabled={zoom === 1}
          title="Reset zoom"
        >
          {String(Math.round(zoom * 100))}%
        </Button>
        <Button
          size="small"
          icon={<Download size={14} />}
          onClick={() => {
            void handleDownloadPNG()
          }}
          title="Download as PNG"
        >
          PNG
        </Button>
        <Button
          size="small"
          icon={<Download size={14} />}
          onClick={handleDownloadSVG}
          title="Download as SVG"
        >
          SVG
        </Button>
        <Button
          size="small"
          icon={<Code size={14} />}
          onClick={() => {
            setShowCode(!showCode)
          }}
          type={showCode ? 'primary' : 'default'}
          title="Toggle code"
        >
          Code
        </Button>
      </Space>

      {/* Show code if toggled */}
      {showCode && (
        <div
          style={{
            marginBottom: 8,
            padding: 12,
            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#f5f5f5',
            borderRadius: 8,
            fontFamily: 'monospace',
            fontSize: '12px',
            whiteSpace: 'pre-wrap',
            position: 'relative',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
            color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'inherit',
          }}
        >
          <Button
            size="small"
            style={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => {
              void handleCopyCode()
            }}
          >
            Copy
          </Button>
          {cleanedChart}
        </div>
      )}

      {/* Diagram container */}
      <div
        ref={svgContainerRef}
        style={{
          padding: 16,
          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#fafafa',
          borderRadius: 8,
          overflow: 'auto',
          maxWidth: '100%',
          maxHeight: '600px',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
        }}
      >
        <div
          style={{
            transform: `scale(${String(zoom)})`,
            transformOrigin: 'top left',
            transition: 'transform 0.2s ease',
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
    </div>
  )
}
